/**
 * Convert keystroke snapshots JSON to VizPI replay events
 * 
 * Input format (from code_snapshots.json):
 * {
 *   "snapshots": [
 *     { "timestamp": "2025-11-18T19:08:24.001Z", "code": "c" },
 *     { "timestamp": "2025-11-18T19:08:24.246Z", "code": "cl" },
 *     ...
 *   ]
 * }
 * 
 * Output format (VizPI CODE_SNAPSHOT events):
 * [
 *   { type: 'CODE_SNAPSHOT', event_type: 'CODE_SNAPSHOT', payload: { code: "c" }, timestamp: 1731956904001 },
 *   ...
 * ]
 */

/**
 * Convert snapshots JSON to VizPI replay events
 * 
 * @param {Object|Array} snapshotsData - Either the full JSON object with { snapshots: [...] } or just the array
 * @returns {Object} - { events: [...], metadata: {...} }
 */
export function convertSnapshotsJsonToReplayFormat(snapshotsData) {
  // Handle both formats: { snapshots: [...] } or just [...]
  const snapshots = Array.isArray(snapshotsData) 
    ? snapshotsData 
    : snapshotsData?.snapshots || [];

  if (snapshots.length === 0) {
    return { 
      events: [], 
      metadata: { 
        duration: 0, 
        eventCounts: {},
        sessionStartTime: 0,
        sessionEndTime: 0,
        totalEvents: 0
      } 
    };
  }

  // Convert each snapshot to a CODE_SNAPSHOT event
  const events = snapshots.map(snapshot => {
    const timestamp = new Date(snapshot.timestamp).getTime();
    
    return {
      type: 'CODE_SNAPSHOT',
      event_type: 'CODE_SNAPSHOT',
      payload: {
        code: snapshot.code || ''
      },
      timestamp,
      sessionId: null
    };
  });

  // Sort by timestamp (should already be sorted, but just in case)
  events.sort((a, b) => a.timestamp - b.timestamp);

  // Calculate metadata
  const sessionStartTime = events[0].timestamp;
  const sessionEndTime = events[events.length - 1].timestamp;

  return {
    events,
    metadata: {
      sessionStartTime,
      sessionEndTime,
      duration: sessionEndTime - sessionStartTime,
      eventCounts: {
        CODE_SNAPSHOT: events.length
      },
      totalEvents: events.length
    }
  };
}

/**
 * Load snapshots from a JSON file and convert to replay format
 * 
 * @param {File} file - File object from input[type="file"]
 * @returns {Promise<Object>} - { events: [...], metadata: {...} }
 */
export async function loadSnapshotsFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        const result = convertSnapshotsJsonToReplayFormat(data);
        resolve(result);
      } catch (err) {
        reject(new Error('Failed to parse JSON file: ' + err.message));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Fetch snapshots from a URL and convert to replay format
 * 
 * @param {string} url - URL to fetch JSON from
 * @returns {Promise<Object>} - { events: [...], metadata: {...} }
 */
export async function loadSnapshotsFromUrl(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch: ${response.status}`);
  }
  const data = await response.json();
  return convertSnapshotsJsonToReplayFormat(data);
}

export default {
  convertSnapshotsJsonToReplayFormat,
  loadSnapshotsFromFile,
  loadSnapshotsFromUrl
};